import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft,
  Calendar,
  Sun,
  Moon,
  Info,
  Coffee,
  MapPin,
  Lock,
  Users
} from 'lucide-react';

// --- TYPES ---
interface AssignmentView {
  id: string;
  assignment_date: string;
  period: 'Q1' | 'Q2' | 'Q3' | 'Q4';
  project_id: string;
  project: { code: string; name: string; address?: string | null; category?: 'production' | 'divers'; } | null;
  divers_sub_project_id?: string | null;
}

interface Teammate {
  employee_id: string;
  assignment_date: string;
  project_id: string;
  employee: { full_name: string } | null;
}

interface DiversSubProject {
  id: string;
  parent_project_id: string;
  divers_number: string;
  name: string;
  address?: string | null;
  is_active: boolean;
}

interface PlanningValidation {
  year: number;
  week_number: number;
}

// --- CONFIGURATION VISUELLE ---
const PERIOD_CONFIG = {
  Q1: { label: 'MATIN (08h - 10h)', icon: <Sun size={18} />, color: '#d97706', bg: '#fffbeb' },     
  Q2: { label: 'MATIN (10h - 12h)', icon: <Coffee size={18} />, color: '#b45309', bg: '#fff7ed' },  
  Q3: { label: 'APRÈS-MIDI (13h - 15h)', icon: <Sun size={18} />, color: '#2563eb', bg: '#eff6ff' }, 
  Q4: { label: 'APRÈS-MIDI (15h - 17h)', icon: <Moon size={18} />, color: '#4f46e5', bg: '#eef2ff' }, 
};

// --- UTILITAIRES ---
const getWeekNumber = (d: Date) => {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

export default function Planning() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState<AssignmentView[]>([]);
  const [diversSubProjects, setDiversSubProjects] = useState<DiversSubProject[]>([]);
  const [teammates, setTeammates] = useState<Teammate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMyPlanning = async () => {
      if (!user) return;
      
      const today = new Date();
      const startStr = today.toISOString().split('T')[0];
      const nextWeek = new Date(today);
      nextWeek.setDate(today.getDate() + 14); // On regarde 2 semaines devant
      const endStr = nextWeek.toISOString().split('T')[0];

      try {
        // 1. Récupérer les affectations brutes (avec divers_sub_project_id)
        const { data: rawData, error } = await supabase
          .from('project_assignments')
          .select(`
            id, assignment_date, period, project_id, divers_sub_project_id,
            project:projects ( code, name, address, category )
          `)
          .eq('employee_id', user.id)
          .gte('assignment_date', startStr)
          .lte('assignment_date', endStr)
          .order('assignment_date', { ascending: true });

        if (error) throw error;

        // 1b. Charger tous les sous-chantiers Divers pour la résolution des noms
        const { data: subData } = await supabase
          .from('divers_sub_projects')
          .select('id, parent_project_id, divers_number, name, address, is_active');

        if (subData) {
          setDiversSubProjects(subData as DiversSubProject[]);
        }

        // Cast explicite pour éviter 'any'
        const rawAssignments = rawData as unknown as AssignmentView[];

        if (!rawAssignments || rawAssignments.length === 0) {
          setAssignments([]);
          setLoading(false);
          return;
        }

        // 2. Vérifier quelles semaines sont "Publiées/Validées"
        // On interroge la table des validations pour récupérer les semaines verrouillées
        const { data: valData } = await supabase
          .from('planning_validations')
          .select('year, week_number')
          .eq('is_locked', true);

        const validations = valData as unknown as PlanningValidation[] || [];

        // Création d'un Set de clés "Année-Semaine" valides pour une recherche rapide O(1)
        const validWeeks = new Set(
          validations.map((v) => `${v.year}-${v.week_number}`)
        );

        // 3. Filtrer : On ne garde que les affectations dont la semaine est présente dans validWeeks
        const publishedAssignments = rawAssignments.filter((a) => {
          const d = new Date(a.assignment_date);
          const w = getWeekNumber(d);
          const y = d.getFullYear();
          return validWeeks.has(`${y}-${w}`);
        });

        setAssignments(publishedAssignments);

        // 4. Charger les collègues via RPC SECURITY DEFINER (bypass RLS)
        const uniqueProjectIds = [...new Set(publishedAssignments.map(a => a.project_id).filter(Boolean))];
        if (uniqueProjectIds.length > 0) {
          const { data: teammateData } = await supabase.rpc('get_planning_teammates', {
            p_project_ids: uniqueProjectIds,
            p_start_date: startStr,
            p_end_date: endStr,
            p_exclude_employee_id: user.id
          });

          if (teammateData) {
            // Adapter le format RPC au format Teammate attendu par l'affichage
            const formatted = (teammateData as { employee_id: string; assignment_date: string; project_id: string; full_name: string }[]).map(t => ({
              employee_id: t.employee_id,
              assignment_date: t.assignment_date,
              project_id: t.project_id,
              employee: { full_name: t.full_name }
            }));
            setTeammates(formatted);
          }
        }

      } catch (err) {
        console.error("Erreur chargement planning", err);
      } finally {
        setLoading(false);
      }
    };

    fetchMyPlanning();
  }, [user]);

  // Grouper par date pour affichage
  const grouped = useMemo(() => {
    return assignments.reduce((acc, curr) => {
      if (!acc[curr.assignment_date]) acc[curr.assignment_date] = [];
      acc[curr.assignment_date].push(curr);
      return acc;
    }, {} as Record<string, AssignmentView[]>);
  }, [assignments]);

  // Trier les périodes (Q1 -> Q4)
  const sortPeriods = (items: AssignmentView[]) => {
    const order = { Q1: 1, Q2: 2, Q3: 3, Q4: 4 };
    return items.sort((a, b) => order[a.period] - order[b.period]);
  };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', padding: '20px', paddingBottom:'100px', maxWidth:'600px', margin:'0 auto' }}>
      
      {/* HEADER */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '30px' }}>
        <button 
          onClick={() => navigate('/')} 
          style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px', cursor: 'pointer', color:'#64748b' }}
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', color: '#1e293b' }}>Mon Planning</h1>
          <div style={{ display:'flex', alignItems:'center', gap:'6px', marginTop:'4px' }}>
            <Lock size={12} color="#10b981" />
            <p style={{ margin: 0, color: '#10b981', fontSize: '0.8rem', fontWeight:'600' }}>Horaires Officiels Validés</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ padding:'60px', textAlign:'center', color:'#94a3b8' }}>Chargement...</div>
      ) : (
        <div style={{ display: 'grid', gap: '20px' }}>
          
          {Object.keys(grouped).length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', background: 'white', borderRadius: '16px', border: '1px dashed #cbd5e1', color: '#64748b' }}>
              <Info size={48} style={{ marginBottom: '15px', opacity: 0.3, margin:'0 auto 15px' }} />
              <p style={{ fontWeight: '600', marginBottom:'5px', color:'#334155' }}>Aucun planning publié.</p>
              <p style={{ fontSize: '0.9rem' }}>
                Soit aucun chantier n'est prévu, soit le planning de la semaine n'a pas encore été validé par la direction.
              </p>
            </div>
          ) : (
            Object.entries(grouped).map(([dateStr, items]) => {
              const date = new Date(dateStr);
              const sortedItems = sortPeriods(items);
              const isToday = dateStr === new Date().toISOString().split('T')[0];

              return (
                <div key={dateStr} style={{ background: 'white', borderRadius: '16px', border: isToday ? '2px solid #2563eb' : '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                  
                  {/* EN-TÊTE JOUR */}
                  <div style={{ background: isToday ? '#eff6ff' : '#f8fafc', padding: '12px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent:'space-between' }}>
                    <div style={{ fontWeight: '700', color: isToday ? '#1e40af' : '#334155', display: 'flex', alignItems: 'center', gap: '10px', textTransform:'capitalize' }}>
                      <Calendar size={18} color={isToday ? '#2563eb' : '#64748b'} />
                      {date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </div>
                    {isToday && <span style={{ fontSize:'0.7rem', background:'#2563eb', color:'white', padding:'2px 8px', borderRadius:'10px', fontWeight:'700' }}>AUJOURD'HUI</span>}
                  </div>
                  
                  {/* LISTE DES CRÉNEAUX */}
                  <div>
                    {sortedItems.map((item, index) => {
                      const style = PERIOD_CONFIG[item.period];
                      const isLast = index === sortedItems.length - 1;

                      // Résolution du nom et de l'adresse pour les sous-chantiers Divers
                      let displayName = item.project?.name || '';
                      let displayAddress = item.project?.address || '';

                      if (item.divers_sub_project_id) {
                        const subProj = diversSubProjects.find(sp => sp.id === item.divers_sub_project_id);
                        if (subProj) {
                          displayName = subProj.name;
                          displayAddress = subProj.address || '';
                        }
                      }

                      return (
                        <div key={item.id} style={{ padding: '16px 20px', borderBottom: isLast ? 'none' : '1px solid #f1f5f9', display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
                          {/* Icône Créneau */}
                          <div style={{ background: style.bg, padding: '10px', borderRadius: '12px', color: style.color, flexShrink: 0 }}>
                            {style.icon}
                          </div>

                          {/* Détails */}
                          <div style={{ flex: 1 }}>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'4px' }}>
                              <span style={{ fontSize: '0.7rem', fontWeight: '700', color: '#64748b', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                                {style.label}
                              </span>
                            </div>

                            <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1e293b', marginBottom:'2px' }}>
                              {displayName}
                            </div>

                            {displayAddress && (
                              <div style={{ color: '#475569', fontSize:'0.9rem', display:'flex', alignItems:'center', gap:'6px' }}>
                                <MapPin size={14} /> {displayAddress}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* ÉQUIPE DU JOUR */}
                  {(() => {
                    const dayProjectIds = [...new Set(items.filter(i => i.project_id).map(i => i.project_id))];
                    const dayTeammates = teammates.filter(t => t.assignment_date === dateStr && dayProjectIds.includes(t.project_id));
                    const uniqueNames = [...new Set(dayTeammates.map(t => t.employee?.full_name).filter(Boolean))];
                    if (uniqueNames.length === 0) return null;
                    return (
                      <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <Users size={14} color="#64748b" />
                          <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Équipe du jour</span>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {uniqueNames.map(name => (
                            <span key={name} style={{ background: '#e0e7ff', color: '#3730a3', padding: '4px 12px', borderRadius: '12px', fontSize: '0.82rem', fontWeight: '600' }}>
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}